/**
 * Returns an async function which can be executed to copy a text
 * from an element programatically.
 * @name copyText
 * @param {Object} app - The app context
 * @returns {Function}
 */
export const copyText = app => {
  /**
   * When executed, this function tries to copy the contents of
   * an element to the clipboard.
   * @param {String} text - The text to be copied
   * @param {String} elementId - The ID of the DOM element
   * @return {Promise}
   */
  return (text, elementId) => {
    function showNotification(type) {
      switch (type) {
        case 'success':
          app.$notify({
            group: 'alert',
            title: 'Copied!',
            text: 'The link has been copied successfully!',
            type: 'success'
          });
          break;
        case 'error':
          app.$notify({
            group: 'alert',
            title: 'Something went wrong!',
            text:
              'Sorry! We were unable to copy the link for you. Please try to copy it manually.',
            type: 'error'
          });
          break;
      }
    }
    const promise = new Promise(async (resolve, reject) => {
      try {
        // Try to copy the text to clipboard using the Cipboard API.
        await navigator.clipboard.writeText(text);
        console.log('Successfully copied text!');
        showNotification('success');
        resolve();
      } catch (clipErr) {
        // In case the Clipboard API is not supported we will use a fallback
        // cuntionality using `document.execCommand('copy')`.
        console.info('Clipboard Exception: ', clipErr);
        const linkInput = document.getElementById(elementId);
        try {
          linkInput.select();
          document.execCommand('copy');
          console.log('Successfully copied text using `execCommand`!');
          showNotification('success');
          resolve();
        } catch (execErr) {
          console.error('Failed to copy text using `execCommand`: ', execErr);
          // Last attempt for mobile devices
          try {
            let range, selection;
            range = document.createRange();
            range.selectNodeContents(linkInput);
            selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            linkInput.setSelectionRange(0, 999999);
            document.execCommand('copy');
            console.log(
              'Successfully copied text using range + selection + `execCommand`!'
            );
            showNotification('success');
            resolve();
          } catch (lastTryError) {
            console.info('Last Try Error: ', lastTryError);
            showNotification('error');
            reject('Programatic copy failed');
          }
        }
      }
    });
    return promise;
  };
};

/**
 * Returns a truncated string
 * @name truncateString
 * @param {String} text - The string to be truncated
 * @param {NUmber} len - The length limit
 * @returns {String} - Truncated string
 */
export const truncateString = (text, len = 20) => {
  if (!text) {
    return '';
  }
  var ext = text.substr(text.lastIndexOf('.') + 1, text.length).toLowerCase();
  var filename = text.replace('.' + ext, '');
  if (text.length <= len) {
    return text;
  }
  filename = filename.substr(0, len) + (text.length > len ? '...' : '');
  return filename;
};

/**
 *
 * Removes hyphens from string.
 * @name removeHyphens
 * @param {String} text - String to remove hyphens
 * @return {String} - New string without hyphens
 */
export const removeHyphens = (text) => {
  return text.replace(/-/g, '');
};

/**
 *
 * Format bits and convert to multiple sizes.
 * @name formatBytes
 * @param {Number} text - Bits
 * @return {String} - Formatted string 100MB
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 *
 * Recursively get folder by ID.
 * @name getFolder
 * @param {Array} - Lilst of folders
 * @param {FolderId} - Folder ID.
 * @return {Object} - Folder Object
 */
export const getFolder = (folders, folderId, level = 1) => {
  let item = null;
  for (let i = 0; i < folders.length; i++) {
    const folder = folders[i];
    if (folder._id === folderId) {
      item = folder;
      break;
    }
    if (!item && folder.folders?.length) {
      level++;
      item = getFolder(folder.folders, folderId, level).folder;
      if (item) {
        break;
      } else {
        level--;
        continue;
      }
    }
  }
  return { folder: item, level };
};

/**
 *
 * Debounce a function exec.
 * @name debounce
 * @param {Function} - Funtion to be debounce.
 * @param {Number} - Delay time.
 * @return {Function} - Debounced function.
 */
export function debounce(func, time = 300) {
  let timer;
  return function() {
    const args = arguments;
    const context = this;
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(context, args);
    }, time);
  };
}


/**
 *
 * Check if is aa mobile device.
 * @name isMobile
 * @return {Boolean} - if is mobile.
 */
export function isMobile() {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    return true;
  }
  return false;
}
